import { ReactNode } from 'react';
import styled from 'styled-components';
import UAParser from 'ua-parser-js';

import { NestedMenuItemOption, BasicMenuItemOption } from 'ui/menu';
import { Skeleton } from 'ui/skeleton';
import { colors, fontSizes, spacing } from 'ui/theme';
import { keyBy } from 'utils/arrayHelpers';
import { convertToLabel, isSystemEvent } from '../utils';
import { EventLabel } from '../../domain';
import { CustomEventName, CUSTOM_LABELS, EVENT_LABELS } from '../../domain/event';
import {
  FILTERS,
  FILTER_NAMES,
  HIDDEN_BY_GMAIL,
  MENU_ID_DELIMITER,
  UNKNOWN,
  USER_AGENT_FILTER_DISPLAY_NAMES,
} from './constants';
import { NoOptionSubMenu } from './NoOptionSubMenu';

// User Agent Parser Utils
export const isGmailUserAgent = (userAgent: string): boolean =>
  userAgent.toLowerCase().indexOf('googleimageproxy') > -1;
export const deduceUserAgent = (userAgent: string, type: string): string => {
  if (isGmailUserAgent(userAgent)) {
    return HIDDEN_BY_GMAIL;
  }

  switch (type) {
    case FILTERS.BROWSER:
      return UAParser(userAgent).browser.name || UNKNOWN;

    case FILTERS.OPERATING_SYSTEM:
      return UAParser(userAgent).os.name || UNKNOWN;

    case FILTERS.DEVICE:
      return UAParser(userAgent).device.type || UNKNOWN;

    default:
      return '';
  }
};
export const getBrowser = (userAgent: string) => deduceUserAgent(userAgent, FILTERS.BROWSER);
export const getOS = (userAgent: string) => deduceUserAgent(userAgent, FILTERS.OPERATING_SYSTEM);
export const getDevice = (userAgent: string) => deduceUserAgent(userAgent, FILTERS.DEVICE);
export const keyByBrowser = (userAgents: string[]) => keyBy(userAgents, getBrowser);
export const keyByOperatingSystem = (userAgents: string[]) => keyBy(userAgents, getOS);
export const keyByDevice = (userAgents: string[]) => keyBy(userAgents, getDevice);

// Menu ID utils
export const createPrefixer = (prefix: string) => (str: string) =>
  `${prefix}${MENU_ID_DELIMITER}${str}`;
export const stripPrefix = (str: string) => str.split(MENU_ID_DELIMITER);
export const getOptionIdForEventType = createPrefixer(FILTERS.EVENT_TYPE);
export const getOptionIdForBrowser = createPrefixer(FILTERS.BROWSER);
export const getOptionIdForOperatingSystem = createPrefixer(FILTERS.OPERATING_SYSTEM);
export const getOptionIdForDevice = createPrefixer(FILTERS.DEVICE);

// Filter option utils
export const createFilterOption = (
  id: string,
  label: string,
  content?: ReactNode,
): BasicMenuItemOption => ({
  id,
  label,
  content,
});

const generateFilterOptions = (options: BasicMenuItemOption[], isLoading: boolean) => {
  if (isLoading) {
    return undefined;
  } else if (options.length === 0) {
    return [
      createFilterOption(
        'No option found',
        'No option found',
        <NoOptionSubMenu>No option found</NoOptionSubMenu>,
      ),
    ];
  }
  return options;
};

const FilterMenuOptionSkeleton = styled(Skeleton)`
  height: ${fontSizes.s16};
  min-width: ${spacing.x4};
  background-color: ${colors.grey1};
`;

// Filter sub-menu utils
export const createSubMenu = (
  filter: FILTERS,
  options: BasicMenuItemOption[],
  isLoading: boolean,
): NestedMenuItemOption => ({
  hasFilter: options.length > 0,
  isSelectMenu: options.length > 0,
  id: filter,
  label: isLoading ? '' : FILTER_NAMES[filter],
  content: isLoading ? <FilterMenuOptionSkeleton /> : null,
  isDisabled: isLoading,
  options: generateFilterOptions(options, isLoading),
});
export const createUserAgentSubMenu = (filter: FILTERS, options: string[], isLoading: boolean) =>
  createSubMenu(
    filter,
    options
      .map((option) =>
        createFilterOption(
          createPrefixer(filter)(option),
          USER_AGENT_FILTER_DISPLAY_NAMES[option] ?? option,
        ),
      )
      .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? '')),
    isLoading,
  );

export const getFilterEventLabel = (event: EventLabel) => {
  const eventLabel = convertToLabel(event);
  return isSystemEvent(eventLabel)
    ? EVENT_LABELS[eventLabel]
    : CUSTOM_LABELS[eventLabel as CustomEventName];
};

export const createSubMenuForEvents = (events: string[], isLoading: boolean) => {
  const eventNames = [
    ...new Set(events.map((event) => (isSystemEvent(event) ? event : CustomEventName.customEvent))),
  ];

  return createSubMenu(
    FILTERS.EVENT_TYPE,
    eventNames
      .map((event) =>
        createFilterOption(
          getOptionIdForEventType(event),
          getFilterEventLabel({ eventType: event }),
        ),
      )
      .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? '')),
    isLoading,
  );
};
